import { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { Divider, Row, Space, Typography } from 'antd';
import { PlusOutlined, RedoOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';
import { AxiosError } from 'axios';
import Button from 'components/new-ui/base/button/Button';
import LabelRequiredSymbol from 'components/new-ui/base/label/LabelRequiredSymbol';
import FormAutocomplete from 'components/new-ui/composed/formAutocomplete/FormAutocomplete';
import FormInput from 'components/new-ui/composed/formInput/FormInput';
import FormSelect from 'components/new-ui/composed/formSelect/FormSelect';
import ConfirmationModal from 'components/new-ui/composed/modal/confirmation-modal/ConfirmationModal';
import { GetIndividualsParams, IndividualType } from 'store/individuals/individual';
import useIndividuals from 'hooks/useIndividuals';
import { DefaultOptionType } from 'antd/lib/select';
import Notification from 'components/ui/base/notification/Notification';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import { createIndividual } from 'store/individuals/client';
import { assignTeamManagerValidationSchema } from '../validation-schemas';
import { AssignTeamManagerFieldValues } from '../types';
import { addIndividualToTeam } from 'store/teams/client';

type Props = {
    customerId: string | undefined;
    teamId: string | null;
    onCancel: () => void;
    onSuccess: () => void;
};

const defaultGetIndividualsParams: Partial<GetIndividualsParams> = {
    type: [IndividualType.TEAM_MANAGER],
};

const AssignTeamManagerModal = ({ teamId, onCancel, onSuccess, customerId }: Props) => {
    const { listIndividuals, individuals } = useIndividuals();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    const [showCreateTeamManagerSection, setShowCreateTeamManagerSection] = useState(false);
    const [teamManagerInput, setTeamManagerInput] = useState('');

    const formik = useFormik<AssignTeamManagerFieldValues>({
        initialValues: {
            teamManagerId: null,
            name: null,
            email: null,
            role: IndividualType.TEAM_MANAGER,
        },
        validationSchema: assignTeamManagerValidationSchema,
        onSubmit: async ({ teamManagerId, name, email, role }) => {
            try {
                if (!teamId || !customerId) {
                    throw new Error('Could not get the team id or customer id');
                }

                showGlobalOverlay({ type: 'LoadingOverlay' });
                if (teamManagerId) {
                    // Assign team manager
                    // Note: With the current setup we can not achieve this flow (we can not select teamManagerId)
                    // 1. We have one team per user (individual)
                    // 2. When we create the user, we are required to select a team
                    // 3. In the select options there should be individuals that don't have any team (if one to one is not changed)
                    await addIndividualToTeam({
                        teamId,
                        individualId: teamManagerId,
                        role: IndividualType.TEAM_MANAGER,
                    });
                    throw new Error('Could not assign team manager to team');
                } else {
                    // Create a new Team Manager with this team id
                    if (!name || !email) {
                        throw new Error('Name and Email are required');
                    }

                    await createIndividual({
                        fullName: name,
                        email,
                        type: role,
                        teamId,
                        customerId,
                        isInvited: true,
                    });

                    Notification({
                        type: 'success',
                        messageTitle: 'Success',
                        placement: 'top',
                        description: 'Successfully created Team Manager for this team',
                    });
                }

                onSuccess();
            } catch (err: unknown) {
                const defaultErrorMessage = 'Something went wrong';
                let errorMessage: string = defaultErrorMessage;
                if (err instanceof Error) errorMessage = err.message;
                if (err instanceof AxiosError) errorMessage = err.response?.data?.message || defaultErrorMessage;

                Notification({
                    type: 'error',
                    messageTitle: 'Error',
                    placement: 'top',
                    description: errorMessage,
                });
            } finally {
                hideGlobalOverlay();
            }
        },
    });

    const debouncedSearchIndividuals = useMemo(
        () =>
            debounce((q: string) => {
                listIndividuals({ ...defaultGetIndividualsParams, query: q });
            }, 300),
        [listIndividuals]
    );

    const teamManagerOptions: DefaultOptionType[] = useMemo(
        () =>
            individuals.list.map((individual) => ({
                value: individual.id,
                label: individual.fullName ?? individual.email,
            })),
        [individuals]
    );

    const handleSelectIndividual = (val: string, option?: DefaultOptionType) => {
        formik.setFieldValue('teamManagerId', val);
        setTeamManagerInput(String(option?.label ?? ''));
        debouncedSearchIndividuals.cancel();
    };

    const handleSearchIndividuals = (val: string) => {
        setTeamManagerInput(val);
        debouncedSearchIndividuals(val);
    };

    const handleClearIndividual = () => {
        setTeamManagerInput('');
        formik.setFieldValue('teamManagerId', undefined);
    };

    useEffect(() => {
        return () => {
            debouncedSearchIndividuals.cancel();
        };
    }, [debouncedSearchIndividuals]);

    // Get initial results for team managers
    useEffect(() => {
        listIndividuals(defaultGetIndividualsParams);
    }, [listIndividuals]);

    return (
        <ConfirmationModal
            open={true}
            icon={<RedoOutlined />}
            title="Assign"
            confirmButtonText="Assign"
            confirmationButtonDisabled={!formik.isValid}
            confirmationButtonLoading={formik.isSubmitting}
            onCancel={onCancel}
            onConfirm={formik.handleSubmit}
            content={
                <>
                    <form onSubmit={formik.submitForm} style={{ width: '100%' }}>
                        <Space
                            direction="vertical"
                            size={16}
                            style={{
                                textAlign: 'left',
                                width: '100%',
                            }}
                        >
                            <Typography.Text>Please select team manager or create new one</Typography.Text>
                            <Row justify="space-between">
                                <label htmlFor="teamManager">
                                    <LabelRequiredSymbol style={{ marginRight: 8 }} />
                                    Assign:
                                </label>
                                <div style={{ maxWidth: 375 }}>
                                    <FormAutocomplete
                                        disabled
                                        id="teamManager"
                                        style={{ width: 375 }}
                                        inputPlaceholder="Please select a team manager"
                                        fieldMeta={formik.getFieldMeta('teamManagerId')}
                                        onBlur={() => formik.setFieldTouched('teamManagerId', true)}
                                        onClear={handleClearIndividual}
                                        onSelect={handleSelectIndividual}
                                        onSearch={handleSearchIndividuals}
                                        options={teamManagerOptions}
                                        value={teamManagerInput}
                                    />
                                </div>
                                <Divider orientation="left">
                                    <Typography style={{ fontSize: 14 }}>or</Typography>
                                </Divider>

                                {showCreateTeamManagerSection ? (
                                    <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                        <Row justify="space-between">
                                            <label htmlFor="name">
                                                <LabelRequiredSymbol style={{ marginRight: 8 }} />
                                                Name:
                                            </label>
                                            <FormInput
                                                inputStyles={{ width: 375 }}
                                                placeholder="Enter Team Manager name"
                                                fieldProps={formik.getFieldProps('name')}
                                                fieldMeta={formik.getFieldMeta('name')}
                                                fieldHelper={formik.getFieldHelpers('name')}
                                            />
                                        </Row>

                                        <Row justify="space-between">
                                            <label htmlFor="email">
                                                <LabelRequiredSymbol style={{ marginRight: 8 }} />
                                                Email:
                                            </label>
                                            <FormInput
                                                inputStyles={{ width: 375 }}
                                                placeholder="Enter email"
                                                fieldProps={formik.getFieldProps('email')}
                                                fieldMeta={formik.getFieldMeta('email')}
                                                fieldHelper={formik.getFieldHelpers('email')}
                                            />
                                        </Row>

                                        <Row justify="space-between">
                                            <label htmlFor="role">
                                                <LabelRequiredSymbol style={{ marginRight: 8 }} />
                                                Role:
                                            </label>
                                            <FormSelect disabled style={{ width: 375 }} value="Team Manager" />
                                        </Row>
                                    </Space>
                                ) : (
                                    <Button
                                        type="link"
                                        icon={<PlusOutlined />}
                                        style={{ marginLeft: 16, color: 'var(--brand-6)' }}
                                        onClick={() => setShowCreateTeamManagerSection(true)}
                                    >
                                        Create Team Manager
                                    </Button>
                                )}
                            </Row>
                        </Space>
                    </form>
                </>
            }
        />
    );
};

export default AssignTeamManagerModal;
